import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from "react-i18next";

import type { NavbarData } from '../App';
import { getItem, removeItem, setItem } from '../utils/storage';

const getNavItems = (t: (key: string) => string) => [
  { label: t('header-home'), href: '#home' },
  { label: t('header-about-us'), href: '#about' },
  { label: t('header-white-paper'), href: '#', hasDropdown: true },
  { label: t('header-contact-us'), href: '#', hasDropdown: true, isContactDropdown: true },
];

const getLanguages = (t: (key: string) => string) => [
  { code: 'fr', label: t('nav-language-fr') },
  { code: 'en', label: t('nav-language-en') },
  { code: 'de', label: t('nav-language-de') },
  { code: 'es', label: t('nav-language-es') },
  { code: 'pt', label: t('nav-language-pt') },
  { code: 'it', label: t('nav-language-it') },
  { code: 'tr', label: t('nav-language-tr') },
  { code: 'ar', label: t('nav-language-ar') },
  { code: 'id', label: t('nav-language-id') },
  { code: 'ms', label: t('nav-language-ms') },
  { code: 'vi', label: t('nav-language-vi') },
  { code: 'el', label: t('nav-language-el') },
  { code: 'cs', label: t('nav-language-cs') },
  { code: 'nl', label: t('nav-language-nl') },
];

export const Header: React.FC<{
  navbarData: NavbarData
}> = ({ navbarData }) => {
  const { t, i18n } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [wpOpen, setWpOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState(t('nav-language-fr'));
  const wpRef = useRef<HTMLDivElement>(null);
  const contactRef = useRef<HTMLDivElement>(null);

  const navItems = getNavItems(t);
  const languages = getLanguages(t);

  const getLang = async () => {
    try {
      const res = await getItem('language')
      if (res) {
        const findIdx = languages.findIndex((item, index) => {
          return item.code === res
        })
        if (findIdx !== -1 && findIdx !== undefined) {
          i18n.changeLanguage(languages[findIdx].code)
          setCurrentLang(languages[findIdx].label)
        }
      }
    } catch { }
  };

  useEffect(() => {
    getLang();
  }, []);

  return (
    <>
      <header className="fixed top-0 left-0 w-full z-50 bg-black/70 backdrop-blur-md border-b border-white/5 supports-[backdrop-filter]:bg-black/60">
        <div className="mx-auto flex max-w-7xl min-h-16 items-center justify-between px-4 pt-[max(0.5rem,env(safe-area-inset-top))] sm:px-6 lg:px-8 md:min-h-20">
          {/* Logo */}
          <a href="#home" className="flex items-center gap-2 shrink-0">
            <img src={new URL('../assets/figma/logo.png', import.meta.url).href} alt="HybridNodes" className="h-8 w-auto" />
          </a>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navItems.map((item) =>
              item.hasDropdown ? (
                item.isContactDropdown
                  ? (navbarData.contactUrl && navbarData.contactUrl.length !== 0) ? (
                    <div
                      key={item.label}
                      ref={contactRef}
                      className="relative"
                      onMouseEnter={() => setContactOpen(true)}
                      onMouseLeave={() => setContactOpen(false)}
                    >
                      <button
                        type="button"
                        onClick={() => setContactOpen(v => !v)}
                        className="flex items-center gap-1 text-base leading-snug text-white transition-colors duration-300 ease-out hover:text-[#8A43FF]"
                      >
                        {item.label}
                        <svg className={`w-4 h-4 transition-transform duration-300 ease-out ${contactOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                      </button>
                      {contactOpen && (
                        <div className="absolute left-1/2 top-full z-50 w-[min(100vw-2rem,16rem)] max-w-[16rem] -translate-x-1/2 pt-2">
                          <div className="bg-[#191919] border border-white/10 rounded-xl overflow-hidden shadow-2xl">
                            {navbarData.contactUrl.map((item, idx) => (
                              <a key={idx} href={item.href} target="_blank" className="block px-5 py-3 text-sm text-white/80 hover:bg-[#8A43FF]/20 hover:text-white transition-colors duration-300 ease-out">
                                {item.label}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : null
                  : (navbarData.whitePaper && navbarData.whitePaper.length !== 0) ? (
                <div
                  key={item.label}
                  ref={wpRef}
                  className="relative"
                  onMouseEnter={() => setWpOpen(true)}
                  onMouseLeave={() => setWpOpen(false)}
                >
                  <button type="button" className="flex items-center gap-1 text-base leading-snug text-white transition-colors duration-300 ease-out hover:text-[#8A43FF]">
                    {item.label}
                    <svg className={`w-4 h-4 transition-transform duration-300 ease-out ${wpOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </button>
                  {wpOpen && (
                    <div className="absolute left-1/2 top-full z-50 w-[min(100vw-2rem,16rem)] max-w-[16rem] -translate-x-1/2 pt-2">
                      <div className="bg-[#191919] border border-white/10 rounded-xl overflow-hidden shadow-2xl">
                        {navbarData.whitePaper.map((wp) => (
                          <a key={wp.label} href={wp.href} target="_blank" className="block px-5 py-3 text-sm text-white/80 hover:bg-[#8A43FF]/20 hover:text-white transition-colors duration-300 ease-out">
                            {wp.label}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : null
              ) : (
                <a key={item.label} href={item.href} className="text-base leading-snug text-white transition-colors duration-300 ease-out hover:text-[#8A43FF]">
                  {item.label}
                </a>
              )
            )}
          </nav>

          {/* Language & Mobile */}
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => setLangOpen(true)}
              className="flex items-center gap-1.5 text-sm font-semibold text-white transition-colors duration-300 ease-out hover:text-[#8A43FF] md:text-lg"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" strokeWidth="1.5" /><path strokeWidth="1.5" d="M2 12h20M12 2c2.5 2.8 4 6.2 4 10s-1.5 7.2-4 10c-2.5-2.8-4-6.2-4-10s1.5-7.2 4-10z" /></svg>
              <span className="hidden sm:inline">{currentLang}</span>
            </button>
            <button type="button" className="md:hidden text-white p-1 transition-opacity duration-300 ease-out hover:opacity-80" onClick={() => setMobileOpen(!mobileOpen)}>
              {mobileOpen ? (
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              ) : (
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden bg-black/95 backdrop-blur-lg border-t border-white/5">
            <nav className="flex flex-col px-6 py-4 gap-1">
              {navItems.map((item) =>
                item.hasDropdown ? (
                  item.isContactDropdown
                    ? (navbarData.contactUrl && navbarData.contactUrl.length !== 0) ? (
                      <div key={item.label}>
                        <button
                          type="button"
                          onClick={() => setContactOpen(!contactOpen)}
                          className="w-full text-left py-3 text-white font-semibold text-lg hover:text-[#8A43FF] transition-colors duration-300 ease-out flex items-center justify-between"
                        >
                          {item.label}
                          <svg className={`w-4 h-4 transition-transform duration-300 ease-out ${contactOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                        </button>
                        {contactOpen && (
                          <div className="pl-4 pb-2">
                            {navbarData.contactUrl.map((item, idx) => (
                              <a
                                key={idx}
                                href={item.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => {
                                  e.preventDefault();
                                  window.open(item.href, '_blank', 'noopener,noreferrer');
                                  setMobileOpen(false);
                                }}
                                className="block py-2 text-sm text-white/70 hover:text-[#8A43FF] transition-colors duration-300 ease-out"
                              >
                                {item.label}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : null
                    : (navbarData.whitePaper && navbarData.whitePaper.length !== 0) ? (
                  <div key={item.label}>
                    <button
                      type="button"
                      onClick={() => setWpOpen(!wpOpen)}
                      className="w-full text-left py-3 text-white font-semibold text-lg hover:text-[#8A43FF] transition-colors duration-300 ease-out flex items-center justify-between"
                    >
                      {item.label}
                      <svg className={`w-4 h-4 transition-transform duration-300 ease-out ${wpOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </button>
                    {wpOpen && (
                      <div className="pl-4 pb-2">
                        {navbarData.whitePaper.map((wp) => (
                          <a
                            key={wp.label}
                            href={wp.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => {
                              e.preventDefault();
                              window.open(wp.href, '_blank', 'noopener,noreferrer');
                              setMobileOpen(false);
                            }}
                            className="block py-2 text-sm text-white/70 hover:text-[#8A43FF] transition-colors duration-300 ease-out"
                          >
                            {wp.label}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ) : null
                ) : (
                  <a key={item.label} href={item.href} onClick={() => setMobileOpen(false)} className="py-3 text-white font-semibold text-lg hover:text-[#8A43FF] transition-colors duration-300 ease-out">
                    {item.label}
                  </a>
                )
              )}
            </nav>
          </div>
        )}
      </header>

      {/* Language Modal */}
      {langOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setLangOpen(false)}>
          <div className="bg-gradient-to-b from-[#0B0044] to-[#5B22DB] rounded-2xl p-6 sm:p-8 w-[90vw] max-w-md shadow-2xl border border-white/10" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-white">{t('header-select-language')}</h3>
              <button type="button" onClick={() => setLangOpen(false)} className="text-white/60 hover:text-white transition-colors duration-300 ease-out">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {languages.map((lang, idx) => (
                <button
                  type="button"
                  key={idx}
                  onClick={async () => {
                    setCurrentLang(lang.label);
                    setLangOpen(false);
                    i18n.changeLanguage(lang.code);
                    await setItem('language', lang.code)
                  }}
                  className={`py-3 px-4 rounded-xl text-sm font-medium transition-[background-color,color,box-shadow] duration-300 ease-out ${currentLang === lang.label
                    ? 'bg-[#8A43FF] text-white shadow-lg shadow-[#8A43FF]/30'
                    : 'bg-white/10 text-white/80 hover:bg-white/20 hover:text-white'
                    }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
